<template>
    <Head title="Your Year in Review" />
    <Bubbles />
    <div class="px-6 sm:px-10 h-screen overflow-y-auto fs-mask flex flex-col">
        <div class="flex flex-col items-center gap-2 sm:gap-3 my-auto">
            <TransitionGroup name="fade">
                <div
                    v-if="currentStep !== WRAPPED_STEPS.SUMMARY"
                    class="flex flex-col items-center justify-center text-center rounded-xl p-5 sm:p-10 gap-6 sm:border-2 sm:border-[#F5F5F5] sm:shadow-lg sm:bg-white mx-6 w-full lg:w-175 overflow-visible"
                    :class="currentPageIndex === PAGES.INTRO ? 'sm:h-125' : 'sm:h-175'">
                    <div v-if="currentStep === WRAPPED_STEPS.SLIDES" class="flex flex-col h-full w-full">
                        <!-- Header section -->
                        <h3
                            v-if="currentHeader"
                            class="text-valence-primary-500 text-center px-4 shrink-0"
                            :class="[1].includes(currentSlidePageNumber) ? 'text-4xl sm:text-5xl font-bold' : 'title-subsection header'">
                            {{ currentHeader }}
                        </h3>
                        <!-- Slide content area - relative container for absolute positioned slides -->
                        <div class="relative flex-1 w-full">
                            <template v-for="(content, i) of wrappedSlides" :key="i">
                                <Transition name="fade">
                                    <!-- Intro card (page 0) - dramatic opener -->
                                    <div v-if="content[0]?.type === 'intro' && currentPageIndex === i" class="absolute inset-0 flex justify-center items-center">
                                        <div class="flex flex-col justify-center items-center text-center gap-6 p-8">
                                            <!-- Intro text - single line that transitions -->
                                            <div class="flex flex-col items-center gap-4">
                                                <Transition name="intro-text" mode="out-in">
                                                    <p :key="introTextIndex" class="text-5xl sm:text-6xl font-bold text-valence-primary-500 text-center">
                                                        {{ currentIntroText }}
                                                    </p>
                                                </Transition>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Achievements card -->
                                    <div
                                        v-else-if="content[0]?.name === 'Achievements' && currentPageIndex === i && achievementsData"
                                        class="absolute inset-0 flex justify-center items-center">
                                        <div class="flex flex-col justify-center items-center text-center w-full max-w-150 px-4 sm:px-0 space-y-6">
                                            <!-- Text sequence - stays visible, last text becomes title -->
                                            <Transition name="intro-text" mode="out-in">
                                                <p :key="achievementsTextIndex" class="text-4xl sm:text-5xl font-semibold text-valence-primary-500 text-center">
                                                    {{ currentAchievementsText }}
                                                </p>
                                            </Transition>
                                            <!-- Achievements reveal -->
                                            <template v-if="showAchievementsReveal">
                                                <div class="space-y-3 sm:space-y-4 w-full max-w-lg px-2 sm:px-0">
                                                    <AnimatedSlideUp v-for="(achievement, idx) in achievementsData" :key="idx" :delay="300 + idx * 300" :duration="400">
                                                        <div class="flex items-center gap-3 sm:gap-4 bg-gray-50 rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-left">
                                                            <span class="text-xl sm:text-2xl shrink-0">✓</span>
                                                            <span class="body-small sm:body text-valence-grey-750">{{ achievement }}</span>
                                                        </div>
                                                    </AnimatedSlideUp>
                                                </div>
                                                <p class="title-body text-valence-grey-750 mt-4">
                                                    <AnimatedFlip value="Look at you go!" :delay="1800" :duration="50" />
                                                </p>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Personality Insights card (Coaching Style - Hero moment) -->
                                    <div
                                        v-else-if="content[0]?.name === 'Personality Insights' && currentPageIndex === i && personalityInsightsData"
                                        class="absolute inset-0 flex flex-col items-center justify-center text-center w-full px-4 sm:px-0">
                                        <div class="flex flex-col items-center justify-center w-full max-w-150">
                                            <!-- Title -->
                                            <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
                                            <h3
                                                class="text-3xl sm:text-4xl font-semibold text-valence-primary-500 transition-all duration-1000 ease-out"
                                                :class="showCoachingLabel ? 'mb-8' : 'mb-4'"
                                                aria-label="What I Learned About You">
                                                <AnimatedFlip value="What I Learned About You" :delay="0" :duration="30" />
                                            </h3>
                                            <!-- "You are..." prefix - large, disappears with transition -->
                                            <Transition name="coaching-insights-fade">
                                                <p v-if="showCoachingLabel" class="text-4xl sm:text-5xl font-semibold text-valence-primary-500 mb-4">
                                                    <AnimatedFlip value="You are..." :delay="800" :duration="30" />
                                                </p>
                                            </Transition>
                                            <!-- Coaching Style Label -->
                                            <p
                                                class="font-bold text-valence-primary-500 transition-all duration-1000 ease-out"
                                                :class="showCoachingLabel ? 'text-4xl sm:text-5xl' : 'text-2xl sm:text-3xl mb-6'">
                                                <AnimatedScalePop :delay="2800" :duration="600"> "{{ personalityInsightsData.coachingStyle }}" </AnimatedScalePop>
                                            </p>
                                            <!-- Insights - appear after "You are..." fades out -->
                                            <div v-if="showInsights" class="space-y-3 sm:space-y-4 w-full max-w-lg px-2 sm:px-0">
                                                <div
                                                    v-for="(insight, idx) in personalityInsightsData.insights"
                                                    :key="idx"
                                                    class="insight-card bg-gray-50 rounded-xl px-4 sm:px-6 py-3 sm:py-4 text-left"
                                                    :style="{ '--insight-delay': `${800 + idx * 1400}ms` }">
                                                    <p class="body-small-bold sm:body-bold text-valence-primary-500">{{ insight.title }}</p>
                                                    <p class="details sm:body-small text-valence-grey-750 mt-1">{{ insight.description }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Superpower card -->
                                    <div
                                        v-else-if="content[0]?.name === 'Superpower' && currentPageIndex === i && superpowerData"
                                        class="absolute inset-0 flex justify-center items-center">
                                        <div class="flex flex-col justify-center items-center text-center w-full max-w-150 px-4 sm:px-0 space-y-6">
                                            <!-- Text sequence before reveal -->
                                            <Transition name="intro-text" mode="out-in">
                                                <p
                                                    v-if="!showSuperpowerReveal"
                                                    :key="superpowerTextIndex"
                                                    class="text-4xl sm:text-5xl font-bold text-valence-primary-500 text-center">
                                                    {{ currentSuperpowerText }}
                                                </p>
                                            </Transition>
                                            <!-- Superpower reveal - superpower pops up first in center, then rest fills in around it -->
                                            <template v-if="showSuperpowerReveal">
                                                <!-- Title appears above (delay 1200) -->
                                                <!-- eslint-disable-next-line vuejs-accessibility/heading-has-content -->
                                                <h3 class="text-4xl sm:text-5xl font-semibold text-valence-primary-500" aria-label="Your Superpower">
                                                    <AnimatedFlip value="Your Superpower" :delay="1200" :duration="30" />
                                                </h3>
                                                <AnimatedScalePop :delay="1800" :duration="400">
                                                    <div class="w-64 h-1.5 bg-valence-primary-500 rounded-full"></div>
                                                </AnimatedScalePop>
                                                <!-- Superpower text appears FIRST as hero moment (delay 0) - in the middle -->
                                                <AnimatedScalePop :delay="0" :duration="800">
                                                    <p class="display-sans-serif text-valence-primary-500 max-w-md">You can {{ superpowerData.superpower }}</p>
                                                </AnimatedScalePop>
                                                <AnimatedSlideUp v-if="superpowerData.evidence" :delay="2400" :duration="500">
                                                    <p class="body sm:title-body text-valence-grey-750 italic max-w-md">We saw this in {{ superpowerData.evidence }}</p>
                                                </AnimatedSlideUp>
                                                <div v-if="superpowerData.personas.length" class="flex flex-wrap justify-center gap-3 mt-4">
                                                    <AnimatedScalePop v-for="(persona, idx) in superpowerData.personas" :key="idx" :delay="3000 + idx * 300" :duration="400">
                                                        <span class="bg-gray-50 text-valence-primary-500 px-4 py-2 rounded-full body-small-semibold sm:body-semibold">
                                                            #{{ persona }}
                                                        </span>
                                                    </AnimatedScalePop>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Future Topics card -->
                                    <div
                                        v-else-if="content[0]?.name === 'Future Topics' && currentPageIndex === i && futureTopicsData"
                                        class="absolute inset-0 flex justify-center items-center overflow-y-auto py-6">
                                        <div class="flex flex-col justify-center items-center text-center w-full max-w-150 px-4 sm:px-0 space-y-5">
                                            <!-- Text sequence - stays visible, last text becomes title -->
                                            <Transition name="intro-text" mode="out-in">
                                                <p :key="futureTopicsTextIndex" class="text-4xl sm:text-5xl font-semibold text-valence-primary-500 text-center">
                                                    {{ currentFutureTopicsText }}
                                                </p>
                                            </Transition>
                                            <!-- Topics reveal -->
                                            <template v-if="showFutureTopicsReveal">
                                                <div class="flex flex-col space-y-3 w-full max-w-lg mx-auto">
                                                    <AnimatedSlideUp v-for="(topic, idx) in futureTopicsData" :key="idx" :delay="200 + idx * 200" :duration="400" class="w-full">
                                                        <div class="future-topic-card bg-gray-50 rounded-xl px-5 py-3.5 text-left w-full">
                                                            <p class="body-small-bold text-valence-primary-500 first-letter:uppercase">{{ topic.topic }}</p>
                                                            <p class="details text-valence-grey-750 mt-1">Because you're great at {{ topic.strength }}</p>
                                                        </div>
                                                    </AnimatedSlideUp>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Use Cases card (How We Can Work Together) -->
                                    <div
                                        v-else-if="content[0]?.name === 'Use Cases' && currentPageIndex === i && useCasesData"
                                        class="absolute inset-0 flex justify-center items-center">
                                        <div class="flex flex-col justify-center items-center text-center w-full max-w-150 px-4 sm:px-0 space-y-6">
                                            <!-- Text sequence animation -->
                                            <Transition name="fade" mode="out-in">
                                                <p :key="useCasesTextIndex" class="text-4xl sm:text-5xl font-semibold text-valence-primary-500">
                                                    {{ currentUseCasesText }}
                                                </p>
                                            </Transition>
                                            <!-- Use cases reveal -->
                                            <template v-if="showUseCasesReveal">
                                                <div class="flex flex-col space-y-3 sm:space-y-4 w-full max-w-lg">
                                                    <AnimatedSlideUp v-for="(useCase, idx) in useCasesData" :key="idx" :delay="400 + idx * 250" :duration="400" class="w-full">
                                                        <div class="bg-gray-50 rounded-xl px-4 sm:px-6 py-4 sm:py-5 text-left w-full">
                                                            <p class="body-bold sm:title-body-bold text-valence-primary-500 mb-1">
                                                                {{ useCasePrefixes[idx] }} {{ useCase.title.toLowerCase() }}
                                                            </p>
                                                            <p class="body-small text-valence-grey-750">{{ useCase.benefit }}</p>
                                                        </div>
                                                    </AnimatedSlideUp>
                                                </div>
                                            </template>
                                        </div>
                                    </div>
                                    <!-- Standard layout for other pages -->
                                    <div
                                        v-else-if="currentPageIndex === i"
                                        class="absolute inset-0 flex overflow-x-auto"
                                        :class="[content.length === 1 ? 'justify-center' : 'justify-between']">
                                        <div
                                            v-for="({ images, title, subtitle, text, call_to_action }, index) of content"
                                            :key="index"
                                            class="flex flex-col justify-center items-center min-w-80 w-80 gap-4">
                                            <div class="flex flex-col justify-center">
                                                <div class="flex grow justify-center items-center">
                                                    <img :src="images[index]" :alt="text" class="mb-8" />
                                                </div>
                                                <h3 v-if="title" class="title mb-6">{{ title }}</h3>
                                                <h4 v-if="subtitle" class="title-subsection text-valence-grey-750 mb-2">{{ subtitle }}</h4>
                                                <p
                                                    v-if="text"
                                                    :class="[title ? 'body-semibold text-valence-grey-750' : 'body-bold', 'text-left mx-auto']"
                                                    style="white-space: pre-line">
                                                    {{ text }}
                                                </p>
                                            </div>
                                            <BaseButton
                                                v-if="call_to_action?.url && call_to_action?.title"
                                                theme="primary"
                                                class="whitespace-nowrap"
                                                @click="handleCTAClick(call_to_action.url)">
                                                <i class="bi bi-bookmark" aria-hidden="true"></i> {{ call_to_action.title }}
                                            </BaseButton>
                                        </div>
                                    </div>
                                </Transition>
                            </template>
                        </div>
                        <!-- Navigation section - fixed at bottom -->
                        <div
                            v-if="currentPageIndex === PAGES.INTRO"
                            class="flex flex-col items-center gap-4 shrink-0 mt-6 transition-opacity duration-500"
                            :class="hasFetchedData && !showIntroGetReady ? 'opacity-100' : 'opacity-0 pointer-events-none'">
                            <BaseButton theme="primary" @click="handleCurrentPageChange(1)"> Continue <i class="bi bi-arrow-right ml-2" aria-hidden="true"></i> </BaseButton>
                            <button class="text-valence-grey-750 body-small hover:underline" @click="handleMaybeLater">Maybe later</button>
                        </div>
                        <div
                            v-if="hasFetchedData && currentPageIndex !== PAGES.INTRO && currentPageIndex === wrappedSlides.length - 1"
                            class="flex gap-10 items-center justify-center shrink-0 mt-6">
                            <button
                                aria-label="Previous slide"
                                class="rounded-full flex items-center justify-center p-1.5 w-8 h-8 bg-slate-300"
                                @click="handleCurrentPageChange(currentPageIndex - 1)">
                                <i class="bi bi-arrow-left text-xl text-valence-primary-500 iconBold" aria-hidden="true"></i>
                            </button>
                            <Transition name="fade">
                                <BaseButton v-if="showUseCasesSummaryButton" theme="primary" @click="handleGoToSummary">
                                    See Your Summary <i class="bi bi-arrow-right ml-2" aria-hidden="true"></i>
                                </BaseButton>
                            </Transition>
                        </div>
                        <div
                            v-if="hasFetchedData && currentPageIndex !== PAGES.INTRO && currentPageIndex !== wrappedSlides.length - 1"
                            class="flex gap-10 items-center justify-center shrink-0 mt-6">
                            <button
                                aria-label="Previous slide"
                                :disabled="currentPageIndex === PAGES.INTRO"
                                class="rounded-full flex items-center justify-center p-1.5 w-8 h-8 bg-slate-300 disabled:opacity-50"
                                @click="handleCurrentPageChange(currentPageIndex - 1)">
                                <i class="bi bi-arrow-left text-xl text-valence-primary-500 iconBold" aria-hidden="true"></i>
                            </button>
                            <span class="font-semibold text-valence-primary-500">{{ currentPageIndex + 1 }} of {{ wrappedSlides.length }}</span>
                            <button
                                aria-label="Next slide"
                                class="rounded-full flex items-center justify-center p-1.5 w-8 h-8 bg-slate-300"
                                @click="() => handleCurrentPageChange(currentPageIndex + 1)">
                                <i class="bi bi-arrow-right text-xl text-valence-primary-500 iconBold" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                    <template v-else-if="currentStep === WRAPPED_STEPS.SCHEDULE">
                        <ScheduleFollowUp :recurring="recurring" :date="date" @recurring-updated="onRecurringUpdate" @date-update="onDateUpdate" />
                        <div class="flex flex-col gap-4">
                            <BaseButton theme="primary" @click="handleSchedule">Sounds good</BaseButton>
                            <BaseButton theme="tertiary" @click="handleSkip">Skip</BaseButton>
                        </div>
                    </template>
                </div>
                <template v-else-if="currentStep === WRAPPED_STEPS.SUMMARY">
                    <AnimatedScalePop v-if="summaryContent" :delay="0" :duration="600">
                        <div class="bg-gray-50 rounded-2xl p-6 sm:p-10 w-full max-w-175 mx-auto space-y-6 sm:space-y-8 border-2 border-[#F5F5F5] shadow-lg">
                            <div class="text-center space-y-2 sm:space-y-3">
                                <h3 class="text-3xl sm:text-5xl font-semibold text-valence-primary-500">Momentum Unlocked</h3>
                                <p class="title-body text-valence-grey-750">Your {{ wrappedYear }} journey</p>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-md sm:max-w-none mx-auto">
                                <div v-if="coachingStyleLabel" class="bg-white rounded-xl px-5 py-4">
                                    <p class="body-bold text-valence-primary-500 mb-1">Your Style</p>
                                    <p class="body-small text-valence-grey-750">"{{ coachingStyleLabel }}"</p>
                                </div>
                                <div v-if="summaryContent.content.focus_area" class="bg-white rounded-xl px-5 py-4">
                                    <p class="body-bold text-valence-primary-500 mb-1">Biggest Focus</p>
                                    <p class="body-small text-valence-grey-750">{{ summaryContent.content.focus_area }}</p>
                                </div>
                                <div class="bg-white rounded-xl px-5 py-4">
                                    <p class="body-bold text-valence-primary-500 mb-1">Super Power</p>
                                    <p class="body-small text-valence-grey-750">{{ summaryContent.content.superpower }}</p>
                                </div>
                            </div>
                            <hr class="border-gray-200" />
                            <!-- Use Cases / How we can work together -->
                            <div class="text-center">
                                <p class="body-bold text-valence-primary-500 mb-4 sm:mb-6">Let's Keep Growing Together</p>
                                <!-- Dynamic use cases from backend -->
                                <div v-if="useCasesData?.length" class="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                                    <button
                                        v-for="(useCase, idx) in useCasesData"
                                        :key="idx"
                                        class="block bg-white rounded-xl px-4 py-3 text-left hover:bg-gray-100 transition-colors flex-1 min-w-35 max-w-50"
                                        @click="handleUseCaseCTA(useCase.ctaUrl)">
                                        <p class="body-small-bold text-valence-primary-500">
                                            {{ useCasePrefixes[idx] }} {{ useCase.title.toLowerCase() }} <i class="bi bi-arrow-right text-xs" aria-hidden="true"></i>
                                        </p>
                                    </button>
                                </div>
                                <!-- Fallback to static features if no use cases data -->
                                <div v-else class="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                                    <button
                                        v-for="(feature, idx) in growthFeatures"
                                        :key="idx"
                                        class="block bg-white rounded-xl px-4 py-3 text-left hover:bg-gray-100 transition-colors flex-1 min-w-35 max-w-50"
                                        @click="handleUseCaseCTA(feature.url)">
                                        <p class="body-small-bold text-valence-primary-500">{{ feature.title }} <i class="bi bi-arrow-right text-xs" aria-hidden="true"></i></p>
                                        <p class="details text-valence-grey-750">{{ feature.benefit }}</p>
                                    </button>
                                </div>
                            </div>
                            <div class="flex justify-center pt-4">
                                <img :src="Logo" alt="Valence logo" class="max-w-28" />
                            </div>
                            <!-- Feedback mechanism -->
                            <div class="flex items-center justify-center gap-4 pt-4" role="group" aria-label="Rate your experience">
                                <span class="body-small text-valence-grey-750">How was your experience?</span>
                                <button
                                    aria-label="Positive feedback"
                                    :aria-pressed="feedbackSent === 'positive'"
                                    class="p-2 text-2xl rounded-full transition-colors"
                                    :class="feedbackSent === 'positive' ? 'bg-green-100' : 'hover:bg-gray-100'"
                                    :disabled="feedbackSent !== null"
                                    @click="submitFeedback('positive')">
                                    👍
                                </button>
                                <button
                                    aria-label="Negative feedback"
                                    :aria-pressed="feedbackSent === 'negative'"
                                    class="p-2 text-2xl rounded-full transition-colors"
                                    :class="feedbackSent === 'negative' ? 'bg-red-100' : 'hover:bg-gray-100'"
                                    :disabled="feedbackSent !== null"
                                    @click="submitFeedback('negative')">
                                    👎
                                </button>
                            </div>
                            <p v-if="feedbackSent" class="body-small text-center text-valence-grey-750">Thanks for your feedback!</p>
                        </div>
                    </AnimatedScalePop>
                    <AnimatedScalePop :delay="700" :duration="500" class="mt-6">
                        <BaseButton theme="tertiary" @click="handleGoToHome">Go to Home</BaseButton>
                    </AnimatedScalePop>
                </template>
            </TransitionGroup>
        </div>
    </div>
</template>

<script setup>
import { Head } from "@inertiajs/vue3";
import * as Sentry from "@sentry/browser";
import Logo from "~img/logo.png";
import etSrc from "~img/wrapped/et.png";
import flagSrc from "~img/wrapped/flag.png";
import homeOfficeSrc from "~img/wrapped/home-office.png";
import purpleSrc from "~img/wrapped/purple.png";
import readingSrc from "~img/wrapped/reading.png";
import scooterSrc from "~img/wrapped/scooter.png";
import selfieSrc from "~img/wrapped/selfie.png";
import { useFetch } from "~vue/api";
import { ChatAction } from "~vue/chatActions";
import AnimatedFlip from "~vue/components/AnimatedFlip.vue";
import AnimatedScalePop from "~vue/components/AnimatedScalePop.vue";
import AnimatedSlideUp from "~vue/components/AnimatedSlideUp.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import Bubbles from "~vue/components/Bubbles.vue";
import ScheduleFollowUp from "~vue/ScheduleFollowUp.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, inject, onMounted, ref, watch } from "vue";

const { $sendEvent } = inject("globalProperties");
const userDetails = inject("user_details");

// These will be set from API response
const wrappedYear = ref(null);

// Static feature list for "Let's Keep Growing Together" section
// Matches features from _calculate_new_features_2025 in wrapped_service.py
const growthFeatures = [
    { title: "Seek Feedback", benefit: "Get anonymous insights from your team", url: "/?open_feedback=true" },
    { title: "Calendar Prep", benefit: "Walk into meetings confident and prepared", url: "/coach/?chat_source=calendar" },
    { title: "My People", benefit: "Deepen your professional relationships", url: "/connections/" },
    { title: "Role Play", benefit: "Practice difficult conversations", url: "/coach/role-play/setup/" },
];

// Coaching style label animation state
const showCoachingLabel = ref(true);
const showInsights = ref(false);
let coachingLabelTimer = null;
let insightsTimer = null;

// Superpower card animation state
const superpowerTextIndex = ref(0);
const superpowerTexts = ["Did you know...", "You have a gift...", "A superpower, if you will!"];
const showSuperpowerReveal = ref(false);
let superpowerTimer = null;

const currentSuperpowerText = computed(() => superpowerTexts[superpowerTextIndex.value]);

// Achievements card animation state
const achievementsTextIndex = ref(0);
const achievementsTexts = ["Now...", "Let's celebrate some of your wins!", "Here's what you accomplished"];
const showAchievementsReveal = ref(false);
let achievementsTimer = null;

const currentAchievementsText = computed(() => achievementsTexts[achievementsTextIndex.value]);

const startAchievementsAnimation = () => {
    const textDelay = 2500;
    const cycleText = () => {
        if (achievementsTextIndex.value < achievementsTexts.length - 1) {
            achievementsTextIndex.value++;
            achievementsTimer = setTimeout(cycleText, textDelay);
        } else {
            // Show achievements after last text
            achievementsTimer = setTimeout(() => {
                showAchievementsReveal.value = true;
            }, 1800);
        }
    };
    setTimeout(cycleText, textDelay);
};

// Future Topics card animation state
const futureTopicsTextIndex = ref(0);
const futureTopicsTexts = ["You've come so far...", "So what's next?"];
const showFutureTopicsReveal = ref(false);
let futureTopicsTimer = null;

const currentFutureTopicsText = computed(() => futureTopicsTexts[futureTopicsTextIndex.value]);

const startFutureTopicsAnimation = () => {
    const textDelay = 2500;
    const cycleText = () => {
        if (futureTopicsTextIndex.value < futureTopicsTexts.length - 1) {
            futureTopicsTextIndex.value++;
            futureTopicsTimer = setTimeout(cycleText, textDelay);
        } else {
            // Show topics after last text
            futureTopicsTimer = setTimeout(() => {
                showFutureTopicsReveal.value = true;
            }, 1800);
        }
    };
    setTimeout(cycleText, textDelay);
};

// Use Cases card animation state
const useCasesTextIndex = ref(0);
const useCasesTexts = ["And finally...", "Let's keep growing together"];
const useCasePrefixes = ["Let me help you", "Together, we can", "We can"];
const showUseCasesReveal = ref(false);
const showUseCasesSummaryButton = ref(false);

const currentUseCasesText = computed(() => useCasesTexts[useCasesTextIndex.value]);

const startUseCasesAnimation = () => {
    const textDelay = 2500;
    const cycleText = () => {
        if (useCasesTextIndex.value < useCasesTexts.length - 1) {
            useCasesTextIndex.value++;
            setTimeout(cycleText, textDelay);
        } else {
            // Show use cases after last text
            setTimeout(() => {
                showUseCasesReveal.value = true;
                // Show "See Your Summary" button after all items animate in
                const itemCount = useCasesData.value?.length || 0;
                const lastItemDelay = 400 + (itemCount - 1) * 250 + 400; // delay + duration
                setTimeout(() => {
                    showUseCasesSummaryButton.value = true;
                }, lastItemDelay);
            }, 1800);
        }
    };
    setTimeout(cycleText, textDelay);
};

// Feedback state
const feedbackSent = ref(null);

const submitFeedback = (type) => {
    feedbackSent.value = type;
    logUserInteraction("wrapped_feedback_submitted", { feedback: type, year: wrappedYear.value });
};

const startSuperpowerAnimation = () => {
    const textDelay = 2500;
    const cycleText = () => {
        if (superpowerTextIndex.value < superpowerTexts.length - 1) {
            superpowerTextIndex.value++;
            superpowerTimer = setTimeout(cycleText, textDelay);
        } else {
            // Show superpower after last text
            superpowerTimer = setTimeout(() => {
                showSuperpowerReveal.value = true;
            }, 1800);
        }
    };
    setTimeout(cycleText, textDelay);
};

// Intro card animation state
const showIntroGetReady = ref(true);
const introTextIndex = ref(0);
const introTexts = ["Get ready...", "In 2025, you built momentum.", "In 2026, let's compound it.", "Let's go."];
let introTimer = null;

const WRAPPED_STEPS = {
    SLIDES: "slides",
    SCHEDULE: "schedule",
    SUMMARY: "summary",
};

const PAGES = {
    INTRO: 0,
    // Note: Metric pages and LLM pages are dynamic based on backend response
    SCHEDULE: 5,
    SUMMARY: 6,
};

// Current intro text to display
const currentIntroText = computed(() => introTexts[introTextIndex.value]);

const startIntroTextAnimation = () => {
    const textDelay = 2500; // Even delay between all parts
    const cycleText = () => {
        if (introTextIndex.value < introTexts.length - 1) {
            introTextIndex.value++;
            setTimeout(cycleText, textDelay);
        } else {
            // Show CTAs after last text
            setTimeout(() => {
                showIntroGetReady.value = false;
            }, 500);
        }
    };
    // Start cycling after first text displays
    setTimeout(cycleText, textDelay);
};

onMounted(() => {
    // Start intro animation on mount
    if (currentPageIndex.value === PAGES.INTRO) {
        startIntroTextAnimation();
    }
});

// hardcoded mapping of images to page number (reordered for new flow)
const getImagesByPage = (page) => {
    switch (+page) {
        case 0: // Intro
            return [homeOfficeSrc];
        case 1: // Personality Insights (Coaching Style) - hero moment
            return [scooterSrc];
        case 2: // Superpower
            return [purpleSrc];
        case 3: // Achievements
            return [selfieSrc, purpleSrc, etSrc];
        case 4: // Future Topics (What's Next)
            return [flagSrc, readingSrc, selfieSrc];
        default:
            return [];
    }
};

const { isFetching, data } = useFetch("/accounts/wrapped/");

// Page 0 is intro, other pages come from API
const pageData = ref([
    {
        content: {
            title: [],
            text: ["intro"], // Special marker for intro card
        },
        page: 0,
        type: "intro",
    },
]);
watch(data, (value) => {
    if (value) {
        const response = JSON.parse(value);
        // Extract year from response and set it
        wrappedYear.value = response.wrapped_year;
        // Add cards to pageData
        pageData.value.push(...response.cards);
    }
});

const hasFetchedData = computed(() => !isFetching.value);
const currentStep = ref(WRAPPED_STEPS.SLIDES);

// Extract achievements data from pageData for custom rendering
const achievementsData = computed(() => {
    const card = pageData.value.find(({ name }) => name === "Achievements");
    if (!card) {
        return null;
    }
    return card.content.text || [];
});

// Extract future topics data from pageData for custom rendering
// New structure: title = topic to explore, subtitle = strength, text = invitation, labels = category tag
const futureTopicsData = computed(() => {
    const card = pageData.value.find(({ name }) => name === "Future Topics");
    if (!card) {
        return null;
    }
    const topics = card.content.title || []; // Topic to explore
    const strengths = card.content.subtitle || []; // User's strength
    const invitations = card.content.text || []; // Invitation text
    const labels = card.content.labels || []; // Category labels
    return topics.map((topic, i) => ({
        label: labels[i] || "",
        topic,
        strength: strengths[i] || "",
        invitation: invitations[i] || "Want to explore this?",
    }));
});

// Extract use cases data from pageData for custom rendering
const useCasesData = computed(() => {
    const card = pageData.value.find(({ name }) => name === "Use Cases");
    if (!card) {
        return null;
    }
    const titles = card.content.title || []; // Benefit headline
    const benefits = card.content.subtitle || []; // Benefit description
    const ctaData = card.content.text || []; // Format: "url|cta_text"
    const labels = card.content.labels || []; // Feature name
    return titles.map((title, i) => {
        const parts = (ctaData[i] || "").split("|");
        return {
            label: labels[i] || "",
            title,
            benefit: benefits[i] || "",
            ctaUrl: parts[0] || "",
            ctaText: parts[1] || "Try it",
        };
    });
});

// Extract personality insights data from pageData for custom rendering
const personalityInsightsData = computed(() => {
    const card = pageData.value.find(({ name }) => name === "Personality Insights");
    if (!card) {
        return null;
    }
    const titles = card.content.title || [];
    const texts = card.content.text || [];
    const coachingStyle = card.content.subtitle?.[0] || null;
    return {
        coachingStyle,
        insights: titles.map((title, i) => ({
            title,
            description: texts[i] || "",
        })),
    };
});

// Coaching style label for summary page
const coachingStyleLabel = computed(() => {
    return personalityInsightsData.value?.coachingStyle || null;
});

// Extract superpower data from pageData for custom rendering
const superpowerData = computed(() => {
    const card = pageData.value.find(({ name }) => name === "Superpower");
    if (!card) {
        return null;
    }
    return {
        superpower: card.content.text?.[0] || "",
        evidence: card.content.text?.[1] || "",
        personas: card.content.subtitle || [],
    };
});

// Map of page number to header text (reordered for new flow)
const headersByPageNumber = {
    0: "", // Intro card - no header
    1: "", // Personality Insights (Coaching Style) - animated inside card, hero moment
    2: "", // Superpower - animated inside card
    3: "", // Achievements - animated inside card
    4: "", // Future Topics (What's Next) - animated inside card
    5: "", // Use Cases - animated inside card
};

const wrappedSlides = computed(() =>
    pageData.value
        .filter(({ type }) => type === "page" || type === "intro")
        .map(({ page, content, call_to_action, type, name }) =>
            content.text
                .filter((e) => e)
                .map((text, index) => ({
                    title: content.title?.[index] ?? content.title,
                    subtitle: content.subtitle?.[index] ?? content.subtitle,
                    text,
                    images: getImagesByPage(page),
                    call_to_action,
                    type,
                    page,
                    name,
                })),
        ),
);
const summaryContent = computed(() => pageData.value.find(({ type }) => type === "summary"));

const currentPageIndex = ref(PAGES.INTRO);

// Watch for page changes to trigger animations
watch(currentPageIndex, (newIndex) => {
    // Clear any existing timers
    if (coachingLabelTimer) {
        clearTimeout(coachingLabelTimer);
    }
    if (insightsTimer) {
        clearTimeout(insightsTimer);
    }
    if (introTimer) {
        clearTimeout(introTimer);
    }
    if (superpowerTimer) {
        clearTimeout(superpowerTimer);
    }
    if (achievementsTimer) {
        clearTimeout(achievementsTimer);
    }
    if (futureTopicsTimer) {
        clearTimeout(futureTopicsTimer);
    }

    const currentSlide = wrappedSlides.value[newIndex];

    // Intro card animation
    if (currentSlide?.[0]?.type === "intro") {
        showIntroGetReady.value = true;
        introTextIndex.value = 0;
        startIntroTextAnimation();
    }

    // Personality Insights card animation - two phases
    if (currentSlide?.[0]?.name === "Personality Insights") {
        showCoachingLabel.value = true;
        showInsights.value = false;
        // Phase 1: After 4s, "You are..." fades out
        coachingLabelTimer = setTimeout(() => {
            showCoachingLabel.value = false;
            // Phase 2: After another 1s, insights appear
            insightsTimer = setTimeout(() => {
                showInsights.value = true;
            }, 1000);
        }, 4000);
    }

    // Superpower card animation
    if (currentSlide?.[0]?.name === "Superpower") {
        superpowerTextIndex.value = 0;
        showSuperpowerReveal.value = false;
        startSuperpowerAnimation();
    }

    // Achievements card animation
    if (currentSlide?.[0]?.name === "Achievements") {
        achievementsTextIndex.value = 0;
        showAchievementsReveal.value = false;
        startAchievementsAnimation();
    }

    // Future Topics card animation
    if (currentSlide?.[0]?.name === "Future Topics") {
        futureTopicsTextIndex.value = 0;
        showFutureTopicsReveal.value = false;
        startFutureTopicsAnimation();
    }

    // Use Cases card animation
    if (currentSlide?.[0]?.name === "Use Cases") {
        useCasesTextIndex.value = 0;
        showUseCasesReveal.value = false;
        showUseCasesSummaryButton.value = false;
        startUseCasesAnimation();
    }
});

// Get the actual page number from the current slide
const currentSlidePageNumber = computed(() => {
    const currentSlide = wrappedSlides.value[currentPageIndex.value];
    return currentSlide?.[0]?.page ?? -1;
});

// Get the header for the current slide based on its actual page number
const currentHeader = computed(() => {
    return headersByPageNumber[currentSlidePageNumber.value] || "";
});

const handleCurrentPageChange = (page) => {
    if (page < 0 || page > wrappedSlides.value.length) {
        return;
    }

    const leavingIntro = currentPageIndex.value === PAGES.INTRO;
    currentPageIndex.value = page;

    if (page === PAGES.INTRO) {
        return;
    }

    if (leavingIntro) {
        logUserInteraction("wrapped_started", { year: wrappedYear.value });
    }

    const pageName = wrappedSlides.value[page]?.[0]?.name || "unknown";
    logUserInteraction("wrapped_screen_view", { screen_index: page, screen_name: pageName });
};

const handleCTAClick = (url) => {
    openUrl(url, { target: "_blank" });
};

// Handle Use Case CTA click - opens in background tab
const handleUseCaseCTA = (url) => {
    const newWindow = window.open(url, "_blank");
    // Attempt to keep focus on current window (browser dependent)
    if (newWindow) {
        newWindow.blur();
        window.focus();
    }
};

const DEFAULT_EVENT_DESCRIPTION = "Let's discuss what's coming up for you this week.";
const recurring = ref(userDetails.value.prefers_weekly_check_in);
const date = ref(new Date());

const onDateUpdate = (newDate) => {
    date.value = newDate;
};
const onRecurringUpdate = (toggled) => {
    recurring.value = toggled;
};

const handleSchedule = async () => {
    try {
        await $sendEvent("schedule_follow_up", {
            event_description: DEFAULT_EVENT_DESCRIPTION,
            event_at_confirmed: DateTime.fromJSDate(date.value).toISO(),
            event_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            recurring: recurring.value,
            type: ChatAction.FOLLOW_UP,
        });
        logUserInteraction(AmplitudeEventNames.FOLLOWUP_SCHEDULED, { source: "wrapped" });
    } catch (e) {
        Sentry.captureException(e);
    }

    currentStep.value = WRAPPED_STEPS.SUMMARY;
    currentPageIndex.value = PAGES.SUMMARY;
};

const handleSkip = () => {
    currentStep.value = WRAPPED_STEPS.SUMMARY;
    currentPageIndex.value = PAGES.SUMMARY;
};

const handleGoToSummary = () => {
    currentStep.value = WRAPPED_STEPS.SUMMARY;
    logUserInteraction("wrapped_screen_view", { screen_index: "summary", screen_name: "Summary" });
};

const handleGoToHome = () => {
    openUrl("/");
};

const handleMaybeLater = () => {
    logUserInteraction("wrapped_maybe_later_clicked", { screen: currentPageIndex.value });
    openUrl("/");
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-fadeIn {
    animation: fadeIn 0.5s ease-out;
}

.gradientBorder::after {
    content: "";
    @apply rounded-xl absolute inset-0 border-2 border-transparent z-0;
    background: linear-gradient(var(--rotate), #ff238f 0%, #555ba2 100%) border-box;
    mask:
        linear-gradient(#fff 0 0) padding-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
}

.header {
    min-height: 2lh;
}

.iconBold::before {
    /* overwrites bootstrap icon font weight */
    font-weight: bold !important;
}

/* Intro card animations */
.intro-title {
    animation: slideUp 0.8s ease-out;
}

.intro-subtitle {
    animation: slideUp 0.8s ease-out 0.3s both;
}

.intro-button {
    animation: slideUp 0.5s ease-out 0.3s both;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Highlight pop animation for percentile badge */
@keyframes highlightPop {
    0% {
        opacity: 0;
        transform: scale(0.8);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

.animate-highlight-pop {
    animation: highlightPop 0.4s ease-out 2.2s both;
}

/* Cascade drop animation for Future Topics */
.future-topic-card {
    position: relative;
    opacity: 0;
    transform: translateY(-80px);
    animation: cascadeDrop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) var(--card-delay, 0ms) forwards;
}

.future-topic-card .topic-content {
    opacity: 0;
    animation: contentFadeIn 0.3s ease-out var(--content-delay, 0ms) forwards;
}

@keyframes cascadeDrop {
    0% {
        opacity: 0;
        transform: translateY(-80px) scale(0.95);
    }
    60% {
        opacity: 1;
        transform: translateY(8px) scale(1);
    }
    80% {
        transform: translateY(-4px) scale(1);
    }
    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes contentFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* Smooth fade out for "You are..." - scale down and fade */
.coaching-insights-fade-leave-active {
    transition: all 1s ease-out;
}

.coaching-insights-fade-leave-to {
    opacity: 0;
    transform: scale(0.8);
}

/* Insight cards - slide in from right with fade */
.insight-card {
    opacity: 0;
    transform: translateX(30px);
    animation: insightSlideIn 0.6s ease-out var(--insight-delay, 0ms) forwards;
}

@keyframes insightSlideIn {
    from {
        opacity: 0;
        transform: translateX(30px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* Intro text transition */
.intro-text-enter-active,
.intro-text-leave-active {
    transition: all 0.25s ease;
}

.intro-text-enter-from {
    opacity: 0;
    transform: translateY(12px);
}

.intro-text-leave-to {
    opacity: 0;
    transform: translateY(-12px);
}
</style>
